import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProNotification } from '@erp/data-access/interfaces';
import { NotificationPanel } from '@/components/notifications/NotificationPanel';
import { NotificationItem } from '@/components/notifications/NotificationItem';

/**
 * Toutes les variantes de notification, côte à côte.
 *
 * Sert de planche de référence design ET de test visuel : la page monte les
 * VRAIS composants (`NotificationPanel`, `NotificationItem`) sur des données
 * factices, donc toute régression de rendu se voit ici avant d'atteindre un
 * électricien. Les libellés passent par le même chemin i18n qu'en production —
 * changer de langue dans l'app change cette page.
 */

// Instant de référence figé : les écarts « il y a 5 min / hier » doivent être
// reproductibles d'un rendu à l'autre.
const NOW = new Date();
const minutesAgo = (m: number) => new Date(NOW.getTime() - m * 60_000).toISOString();

let seq = 0;
function mock(
  type: string,
  metadata: Record<string, unknown> | null,
  opts: {
    minutes?: number;
    read?: boolean;
    title?: string;
    body?: string | null;
    linkUrl?: string | null;
  } = {},
): ProNotification {
  seq += 1;
  return {
    id: `preview-${seq}`,
    tenantId: 'preview-tenant',
    proCustomerId: 'preview-customer',
    type,
    title: opts.title ?? 'Titre écrit par le serveur',
    body: opts.body === undefined ? 'Corps écrit par le serveur' : opts.body,
    linkUrl: opts.linkUrl ?? null,
    readAt: opts.read ? minutesAgo(1) : null,
    createdAt: minutesAgo(opts.minutes ?? 5),
    metadata,
  };
}

interface PreviewGroup {
  id: string;
  label: string;
  hint: string;
  items: Array<{ note: string; notification: ProNotification }>;
}

function buildGroups(): PreviewGroup[] {
  return [
    {
      id: 'documents',
      label: 'Documents',
      hint: 'Devis et commandes — déclenchés par les triggers sur pro_devis / pro_commandes.',
      items: [
        {
          note: 'devis_created',
          notification: mock('devis_created', {
            code: 'DV-2026-0042',
            totalTtc: 1250.5,
            commission: 125.05,
          }, { minutes: 3 }),
        },
        {
          note: 'devis_validated',
          notification: mock('devis_validated', {
            code: 'DV-2026-0042',
            totalTtc: 1250.5,
            commission: 125.05,
          }, { minutes: 42 }),
        },
        {
          note: 'devis_rejected',
          notification: mock('devis_rejected', { code: 'DV-2026-0039' }, { minutes: 180 }),
        },
        {
          note: 'commande_created',
          notification: mock('commande_created', {
            code: 'CMD-2026-0117',
            totalTtc: 2340.75,
            commission: 234.075,
            deliveryFee: 7,
          }, { minutes: 90 }),
        },
        {
          note: 'product_requests_resolved',
          notification: mock('product_requests_resolved', {
            devisCode: 'DV-2026-0044',
            fulfilled: 2,
            unavailable: 1,
          }, { minutes: 300 }),
        },
      ],
    },
    {
      id: 'order-status',
      label: 'Statut de commande — les 7 états',
      hint: 'Nouveau type `commande_status` : chaque changement de statut est notifié, avec la frise du parcours.',
      items: (
        [
          ['en_attente', 15],
          ['confirmee', 30],
          ['emballee', 120],
          ['expediee', 400],
          ['livree', 1500],
          ['retournee', 2900],
          ['rejetee', 4400],
        ] as const
      ).map(([status, minutes]) => ({
        note: `commande_status · ${status}`,
        notification: mock('commande_status', {
          code: 'CMD-2026-0117',
          status,
          totalTtc: 2340.75,
          commission: 234.075,
        }, { minutes }),
      })),
    },
    {
      id: 'money',
      label: 'Solde et commission',
      hint: 'Le montant passe en gros, et le solde du portefeuille APRÈS le mouvement est calculé côté serveur.',
      items: [
        {
          note: 'commission_approved',
          notification: mock('commission_approved', {
            amount: 234.075,
            referenceCode: 'CMD-2026-0117',
            balanceAfter: 1820.35,
          }, { minutes: 8 }),
        },
        {
          note: 'commission_rejected',
          notification: mock('commission_rejected', {
            amount: 78.5,
            referenceCode: 'CMD-2026-0104',
            balanceAfter: 1586.275,
          }, { minutes: 1400 }),
        },
      ],
    },
    {
      id: 'insights',
      label: 'Bilans périodiques',
      hint: 'Générés par cron : bilan des gains le 1er du mois, stats clients chaque lundi.',
      items: [
        {
          note: 'monthly_recap · en hausse',
          notification: mock('monthly_recap', {
            period: '2026-07',
            periodStart: '2026-07-01',
            commission: 1642.5,
            commissionValidated: 1180,
            commissionPending: 462.5,
            previousCommission: 1095,
            orders: 14,
            revenueTtc: 18420.75,
            balance: 1820.35,
          }, { minutes: 60 }),
        },
        {
          note: 'monthly_recap · en baisse',
          notification: mock('monthly_recap', {
            period: '2026-06',
            periodStart: '2026-06-01',
            commission: 620,
            commissionValidated: 620,
            commissionPending: 0,
            previousCommission: 1240,
            orders: 5,
            revenueTtc: 7100,
          }, { minutes: 44_000 }),
        },
        {
          note: 'clients_digest',
          notification: mock('clients_digest', {
            period: '2026-W31',
            periodStart: '2026-07-27',
            clients: 23,
            newClients: 3,
            activeClients: 9,
            orders: 12,
            revenueTtc: 6480.25,
            topClientName: 'Résidence El Manar',
            topClientRevenue: 2140,
          }, { minutes: 1500 }),
        },
      ],
    },
    {
      id: 'catalog',
      label: 'Catalogue et formation',
      hint: 'Diffusions regroupées sur 1 h : publier 200 produits ne crée qu’une seule ligne qui compte.',
      items: [
        {
          note: 'formation_video_published · une vidéo',
          notification: mock('formation_video_published', {
            videosCount: 1,
            videoTitle: 'Poser un interrupteur différentiel',
            chapterTitle: 'Tableau électrique',
            durationSeconds: 384,
          }, { minutes: 20 }),
        },
        {
          note: 'formation_video_published · regroupées',
          notification: mock('formation_video_published', {
            videosCount: 4,
            chapterTitle: 'Domotique',
          }, { minutes: 2600 }),
        },
        {
          note: 'product_published · un produit',
          notification: mock('product_published', {
            productsCount: 1,
            sampleNames: ['Disjoncteur différentiel 30mA 40A'],
          }, { minutes: 12 }),
        },
        {
          note: 'product_published · regroupés',
          notification: mock('product_published', {
            productsCount: 27,
            sampleNames: [
              'Disjoncteur 16A',
              'Câble H07V-U 2,5 mm²',
              'Interrupteur va-et-vient',
              'Gaine ICTA 20',
            ],
          }, { minutes: 3000 }),
        },
      ],
    },
    {
      id: 'resilience',
      label: 'Cas limites',
      hint: 'Ce que la cloche fait quand les faits manquent — elle ne doit jamais casser ni afficher de vide.',
      items: [
        {
          note: 'type inconnu → repli sur le texte serveur',
          notification: mock('un_type_du_futur', { peu_importe: true }, {
            minutes: 10,
            title: 'Type inconnu du client',
            body: 'Le serveur a écrit ce texte, on l’affiche tel quel.',
          }),
        },
        {
          note: 'metadata vide → texte seul, sans puces',
          notification: mock('commande_status', null, {
            minutes: 25,
            title: 'Commande confirmée',
            body: 'La commande CMD-2026-0117 est confirmée.',
          }),
        },
        {
          note: 'déjà lue',
          notification: mock('commission_approved', {
            amount: 45.75,
            referenceCode: 'CMD-2026-0088',
            balanceAfter: 1352.2,
          }, { minutes: 5000, read: true }),
        },
      ],
    },
  ];
}

export function NotificationsPreviewPage() {
  const { t, i18n } = useTranslation();
  const [forceRead, setForceRead] = useState(false);
  const groups = useMemo(buildGroups, []);

  const all = useMemo(
    () =>
      groups
        .flatMap((g) => g.items.map((i) => i.notification))
        .map((n) => (forceRead ? { ...n, readAt: minutesAgo(1) } : n)),
    [groups, forceRead],
  );

  const unreadCount = all.filter((n) => n.readAt === null).length;

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1
            className="text-[22px] font-semibold"
            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
          >
            Notifications — toutes les variantes
          </h1>
          <p className="text-[13px] mt-1" style={{ color: 'var(--pro-text-grey)' }}>
            {all.length} variantes · langue active :{' '}
            <strong>{i18n.language}</strong> · les libellés passent par le même
            chemin i18n qu’en production.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['fr', 'en', 'ar'] as const).map((lng) => (
            <button
              key={lng}
              type="button"
              onClick={() => void i18n.changeLanguage(lng)}
              className="text-[12px] font-semibold px-3 py-1.5 rounded-lg uppercase"
              style={{
                background:
                  i18n.language === lng
                    ? 'var(--pro-primary-subtle)'
                    : 'white',
                color:
                  i18n.language === lng
                    ? 'var(--pro-primary-text)'
                    : 'var(--pro-text-grey)',
                border: '1px solid var(--pro-border)',
              }}
            >
              {lng}
            </button>
          ))}
          <label
            className="flex items-center gap-2 text-[12px] px-3 py-1.5 rounded-lg bg-white cursor-pointer"
            style={{ border: '1px solid var(--pro-border)' }}
          >
            <input
              type="checkbox"
              checked={forceRead}
              onChange={(e) => setForceRead(e.target.checked)}
            />
            Tout marquer comme lu
          </label>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[380px_1fr] items-start">
        {/* La cloche telle qu'elle apparaît vraiment */}
        <div className="lg:sticky lg:top-4">
          <p
            className="text-[12px] font-semibold uppercase tracking-wide mb-2"
            style={{ color: 'var(--pro-text-grey-light)' }}
          >
            Le panneau complet
          </p>
          <div
            className="rounded-2xl bg-white shadow-xl overflow-hidden"
            style={{ border: '1px solid var(--pro-border)' }}
          >
            <NotificationPanel
              notifications={all}
              unreadCount={unreadCount}
              now={NOW}
              onMarkAllRead={() => setForceRead(true)}
              maxHeight={560}
            />
          </div>
          <p
            className="text-[11px] mt-2 leading-relaxed"
            style={{ color: 'var(--pro-text-grey-light)' }}
          >
            Groupement par jour, filtre Tout / Non lues, en-tête collant.
            {' '}
            {t('pro.notif.filter_unread', 'Non lues')} = {unreadCount}.
          </p>
        </div>

        {/* Chaque variante, annotée */}
        <div className="space-y-6">
          {groups.map((group) => (
            <section
              key={group.id}
              className="rounded-2xl bg-white overflow-hidden"
              style={{ border: '1px solid var(--pro-border)' }}
            >
              <div
                className="px-4 py-3"
                style={{
                  borderBottom: '1px solid var(--pro-line)',
                  background: 'var(--pro-grey-subtle)',
                }}
              >
                <h2
                  className="text-[14px] font-semibold"
                  style={{ color: 'var(--pro-text-dark)' }}
                >
                  {group.label}
                </h2>
                <p
                  className="text-[12px] mt-0.5"
                  style={{ color: 'var(--pro-text-grey)' }}
                >
                  {group.hint}
                </p>
              </div>
              {group.items.map((item) => (
                <div key={item.notification.id}>
                  <div
                    className="px-4 pt-2 text-[10px] font-mono uppercase tracking-wide"
                    style={{ color: 'var(--pro-text-grey-light)' }}
                  >
                    {item.note}
                  </div>
                  <NotificationItem
                    notification={
                      forceRead
                        ? { ...item.notification, readAt: minutesAgo(1) }
                        : item.notification
                    }
                    now={NOW}
                  />
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

export default NotificationsPreviewPage;
